import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Stethoscope, ShieldCheck } from "@phosphor-icons/react";

/**
 * Kiosk (QR code) landing — 2 steps:
 *   1. Read intro (practitioner + questionnaire title)
 *   2. Fill patient info (nom, prénom, date de naissance, email optionnel) →
 *      backend creates/reuses the patient record and returns an assignment token.
 */
export default function Kiosk() {
  const { publicToken } = useParams();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("intro"); // "intro" | "form"
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");
  const [email, setEmail] = useState("");
  const [fieldErr, setFieldErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/kiosk/${publicToken}`);
        setInfo(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Questionnaire introuvable");
      }
    })();
  }, [publicToken]);

  const submit = async () => {
    setFieldErr("");
    if (!firstName.trim() || !lastName.trim() || !dob) {
      return setFieldErr("Prénom, nom et date de naissance sont obligatoires.");
    }
    if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      return setFieldErr("Adresse email invalide.");
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/public/kiosk/${publicToken}/start`, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        date_of_birth: dob,
        email: email.trim(),
      });
      nav(`/q/${data.token}`, { replace: true });
    } catch (e) {
      setFieldErr(e?.response?.data?.detail || "Impossible de démarrer");
      setBusy(false);
    }
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F7FB] p-8">
      <div className="max-w-md text-center">
        <p className="font-serif text-3xl mb-4 text-[#1E3A5F]">Indisponible</p>
        <p className="text-[#5F758F]">{error}</p>
      </div>
    </div>
  );
  if (!info) return null;

  return (
    <div className="min-h-screen bg-[#F4F7FB] flex flex-col">
      <header className="pt-8 pb-4 px-6 flex items-center gap-2">
        <Stethoscope size={22} weight="duotone" className="text-[#3A8FA5]" />
        <span className="font-serif text-xl text-[#1E3A5F]">Questiomed</span>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-10">
        {step === "intro" ? (
          <div className="max-w-2xl w-full text-center">
            <p className="text-xs tracking-[0.3em] uppercase text-[#3A8FA5] mb-6">Salle d'attente</p>
            <h1 className="font-serif text-5xl md:text-6xl mb-6 text-[#1E3A5F] leading-tight">
              {info.template.title}
            </h1>
            {info.template.category && (
              <p className="text-sm text-[#5F758F] mb-4">{info.template.category}</p>
            )}
            <p className="text-lg text-[#5F758F] leading-relaxed mb-12">
              Merci de prendre quelques minutes pour compléter ce questionnaire. Vos réponses seront transmises à <span className="text-[#1E3A5F] font-medium">{info.practitioner_name}</span>.
            </p>
            <Button
              size="lg"
              onClick={() => setStep("form")}
              className="rounded-full bg-[#3A8FA5] hover:bg-[#164E5F] text-white px-12 py-8 text-lg"
              data-testid="kiosk-start"
            >
              Commencer
            </Button>
          </div>
        ) : (
          <div className="max-w-md w-full bg-white border border-[#E1E9F0] rounded-2xl p-8" data-testid="kiosk-patient-form">
            <p className="text-xs tracking-[0.3em] uppercase text-[#3A8FA5] mb-3">Vos informations</p>
            <h2 className="font-serif text-3xl text-[#1E3A5F] mb-2 leading-tight">Qui remplit ce questionnaire ?</h2>
            <p className="text-sm text-[#5F758F] mb-6">Ces informations permettent à {info.practitioner_name} de retrouver vos réponses dans votre dossier.</p>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Prénom</Label>
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="mt-2 rounded-[10px]" placeholder="Marie" data-testid="kiosk-first-name" />
              </div>
              <div>
                <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Nom</Label>
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)} className="mt-2 rounded-[10px]" placeholder="Dupont" data-testid="kiosk-last-name" />
              </div>
            </div>
            <div className="mb-3">
              <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Date de naissance</Label>
              <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} className="mt-2 rounded-[10px]" data-testid="kiosk-dob" />
            </div>
            <div className="mb-5">
              <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Email <span className="normal-case tracking-normal text-[#5F758F]">(optionnel)</span></Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 rounded-[10px]" placeholder="marie.dupont@example.com" data-testid="kiosk-email" />
            </div>

            <div className="rounded-[10px] bg-[#F4F7FB] border border-[#E1E9F0] px-4 py-3 mb-5 flex items-start gap-2.5">
              <ShieldCheck size={18} weight="duotone" className="text-[#3A8FA5] shrink-0 mt-0.5" />
              <p className="text-[12px] text-[#5F758F] leading-relaxed">Vos données de santé sont hébergées en France chez un hébergeur agréé HDS et ne sont accessibles qu'à votre praticien.</p>
            </div>

            {fieldErr && <p className="text-[13px] text-[#8B2A1E] font-medium mb-3" data-testid="kiosk-error">{fieldErr}</p>}

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep("intro")} disabled={busy} className="rounded-[10px] font-semibold">Retour</Button>
              <Button onClick={submit} disabled={busy} className="flex-1 rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold" data-testid="kiosk-submit">
                {busy ? "…" : "Continuer →"}
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
